import React from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * DateValidationError
 *
 * Displays an error alert when campaign dates fail launch validation.
 * Provides two auto-adjust strategies and a link to the Campaign Requester.
 *
 * Extracted from CampaignOrderQueueCampaignReview.js to reduce god-component size.
 */
const DateValidationError = ({
  dateValidationErrors,
  isAutoAdjusting,
  onAutoAdjust,
  onOpenRequester,
  onDismiss,
}) => {
  if (!dateValidationErrors || dateValidationErrors.length === 0) {
    return null;
  }

  const hasOrderError = dateValidationErrors.some((e) => e.level === 'CAMPAIGN_ORDER');
  const hasCampaignError = dateValidationErrors.some((e) => e.level === 'CAMPAIGN');

  const requesterTarget = hasOrderError
    ? 'Campaign Order Details'
    : hasCampaignError
    ? 'Campaign Details'
    : 'Line Items';

  return (
    <Alert
      message="Date Warning: Past Dates Detected"
      description={
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text>
            The following date issues were detected. You can auto-adjust them or dismiss to proceed with launch:
          </Text>
          <ul style={{ marginTop: 8, marginBottom: 8 }}>
            {dateValidationErrors.map((error, index) => (
              <li key={index}>
                <Text strong>{error.entityName}</Text>: {error.message}
              </li>
            ))}
          </ul>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Dates can be updated in Campaign Requester → {requesterTarget}, or use Auto-Adjust below.
          </Text>

          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
            <Button
              type="primary"
              icon={<CalendarOutlined />}
              loading={isAutoAdjusting}
              onClick={() => onAutoAdjust('shorten')}
              block
            >
              Auto-Adjust: Keep Original End Dates
            </Button>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Moves start dates to today. Campaign will be shorter than originally planned.
            </Text>

            <Button
              type="default"
              icon={<CalendarOutlined />}
              loading={isAutoAdjusting}
              onClick={() => onAutoAdjust('preserve-duration')}
              block
            >
              Auto-Adjust: Preserve Duration
            </Button>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Shifts all dates forward equally. Campaign runs for the same number of days.
            </Text>
          </Space>

          <Button
            type="link"
            icon={<InfoCircleOutlined />}
            onClick={onOpenRequester}
            style={{ padding: 0, marginTop: 4 }}
          >
            Open Campaign Requester
          </Button>
          <Button
            type="text"
            size="small"
            onClick={onDismiss}
            style={{ marginTop: 8 }}
          >
            Dismiss
          </Button>
        </Space>
      }
      type="warning"
      showIcon
      closable
      onClose={onDismiss}
      style={{ marginBottom: 16 }}
    />
  );
};

export default DateValidationError;
